import { useMemo, type CSSProperties } from 'react';
import { useLazyInView } from '../hooks/useLazyInView';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useTokens,
  usePropertyField,
  RichTextContent,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { contrastRatio, getContrastTextColor } from '../utils/luminance';
import {
  brandPatternDataUri,
  brandPatternTileSize,
  PATTERN_UNIT_DESKTOP,
  PATTERN_UNIT_MOBILE,
} from './_brandPattern';

// ─── Slot group definitions (inline literals — required by the schema linter) ──

const ctaDef: SlotGroupDefinition = {
  label: 'CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/neighborhood' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'Explore Neighborhood' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// ─── Fixed brand-pattern background ────────────────────────────────────────────
//
// The same brand line pattern as <PatternBand>, rendered as an SVG data-URI so
// it can tile as a CSS background with `background-attachment: fixed` (the
// texture stays put while the section scrolls over it). The stroke color is the
// resolved secondary token, so it stays on-brand across themes.

// ─── Google Maps embed ─────────────────────────────────────────────────────────

const MAPS_API_KEY =
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.GOOGLE_MAPS_API_KEY) || '';

// ─── Component ────────────────────────────────────────────────────────────────

export function PinnedCallouts() {
  useRevealStyles();
  const revealRef = useScrollReveal<HTMLDivElement>();

  const tokens = useTokens();
  const secondaryColor = tokens?.color?.secondary || '#B28C59';
  // Desktop (≥ lg) uses the standard unit; mobile + tablet (< lg) a smaller one.
  const patternBgDesktop = useMemo(() => brandPatternDataUri(secondaryColor, PATTERN_UNIT_DESKTOP), [secondaryColor]);
  const patternBgMobile = useMemo(() => brandPatternDataUri(secondaryColor, PATTERN_UNIT_MOBILE), [secondaryColor]);
  const tileDesktop = brandPatternTileSize(PATTERN_UNIT_DESKTOP);
  const tileMobile = brandPatternTileSize(PATTERN_UNIT_MOBILE);

  // The body copy sits over the `~88% surface` muting overlay, so it effectively
  // reads against `--vibe-color-surface`. The default `--vibe-color-text` is
  // paired with the page background, so a dark `surface` (property_color_3)
  // leaves the copy dark-on-dark. Keep the themed text when it clears WCAG AA
  // against surface; otherwise fall back to a luminance-picked black/white.
  // Mirrors the banner-text fix in AmenitiesFeatures.
  const bodyTextStyle = useMemo<CSSProperties | undefined>(() => {
    const surface = tokens?.color?.surface;
    if (!surface) return undefined;
    const themed = tokens?.color?.text;
    const color = themed && contrastRatio(surface, themed) >= 4.5 ? themed : getContrastTextColor(surface);
    return { ['--pc-body-text' as string]: color } as CSSProperties;
  }, [tokens]);

  const cta = useSlotGroup('pinnedcallouts.cta', ctaDef);
  const [mapRef, mapVisible] = useLazyInView<HTMLDivElement>();

  // Property-centred map — mirrors ContactMap's embed logic.
  const lat = usePropertyField('property_address_latitude') as string | null;
  const lng = usePropertyField('property_address_longitude') as string | null;
  const address = usePropertyField('property_full_address') as string | null;

  const hasCoords = !!lat && !!lng && lat.trim() !== '' && lng.trim() !== '';

  let mapSrc = '';
  if (hasCoords && MAPS_API_KEY) {
    mapSrc = `https://www.google.com/maps/embed/v1/place?key=${MAPS_API_KEY}&q=${lat},${lng}&zoom=14`;
  } else if (hasCoords) {
    mapSrc = `https://maps.google.com/maps?q=${lat},${lng}&z=14&output=embed`;
  } else if (address && address.trim() !== '') {
    mapSrc = `https://maps.google.com/maps?q=${encodeURIComponent(address.trim())}&output=embed`;
  }

  return (
    <section className="relative w-full overflow-hidden">
      {/* ── Fixed brand-pattern texture (z-0) ── */}
      {/* Mobile + tablet (< lg): smaller unit; scrolls with the section. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 z-0 bg-scroll lg:hidden"
        style={{
          backgroundImage: patternBgMobile,
          backgroundRepeat: 'repeat',
          backgroundSize: `${tileMobile}px ${tileMobile}px`,
        }}
      />
      {/* Desktop (≥ lg): texture stays put while the section scrolls. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 z-0 bg-fixed hidden lg:block"
        style={{
          backgroundImage: patternBgDesktop,
          backgroundRepeat: 'repeat',
          backgroundSize: `${tileDesktop}px ${tileDesktop}px`,
        }}
      />
      {/* ── Muting overlay (z-[1]) — keeps the copy readable over the texture ── */}
      <div
        aria-hidden="true"
        className="absolute inset-0 z-[1] bg-[color-mix(in_srgb,var(--vibe-color-surface)_88%,transparent)]"
      />

      {/* ── Content (z-10) ── */}
      <div
        ref={revealRef}
        style={bodyTextStyle}
        className="reveal-fade-up relative z-10 grid grid-cols-1 md:grid-cols-2 items-stretch gap-10 md:gap-0 py-[50px] md:py-[70px] lg:py-[50px]"
      >
        {/* Map column (full bleed to the left/top/bottom edges).
            On mobile it drops below the copy (order-2); desktop keeps it left (order-1). */}
        <div
          ref={mapRef}
          className="order-2 md:order-1 relative w-full h-full overflow-hidden min-h-[360px] md:min-h-[520px] bg-[var(--vibe-color-surface)]"
        >
          {mapSrc && mapVisible ? (
            <iframe
              className="absolute inset-0 w-full h-full border-0"
              src={mapSrc}
              title="Property location map"
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          ) : mapSrc ? null : (
            <div className="absolute inset-0 flex items-center justify-center">
              <p className="font-body text-sm text-[var(--vibe-color-text-muted)]">
                Map location not available
              </p>
            </div>
          )}
        </div>

        {/* Text column — heading/copy/CTA. Sits above the map on mobile (order-1),
            right column on desktop (order-2). */}
        <div className="order-1 md:order-2 flex flex-col justify-center px-4 sm:px-6 md:px-8 lg:px-12">
          <div className="w-full md:max-w-2xl md:mr-auto md:pl-4 lg:pl-8">
            <RichTextContent
              path="pinnedcallouts.heading"
              label="Heading"
              defaultValue="<p>Find Your Island Escape</p>"
              as="h2"
              className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] uppercase mb-4"
              style={{ color: '#fff' }}
            />

            <RichTextContent
              path="pinnedcallouts.body"
              label="Body Text"
              defaultValue="<p>Perfectly positioned near the best of the neighborhood, our community puts dining, shopping, and entertainment just minutes from your front door. Whether you're commuting to work or exploring the city, everything you need is within easy reach.</p>"
              as="div"
              className="font-body text-base text-white leading-relaxed [&_a]:underline mb-6"
            />

            <EditableSlotGroup groupPath="pinnedcallouts.cta" definition={ctaDef}>
              <a
                href={cta.url || '/neighborhood'}
                target={cta._target || '_self'}
                className="pc-cta inline-flex items-center justify-center whitespace-nowrap px-6 py-3 font-heading text-sm md:px-4 md:tracking-[0.1em] lg:px-12 lg:py-4 lg:text-base lg:tracking-[0.15em] uppercase tracking-[0.15em] transition-all"
              >
                {cta.label || 'Explore Neighborhood'}
              </a>
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      <style>{`
        /* Solid pill CTA — filled with the theme primary, matching the
           reference stripe's "Explore Neighborhood" button. */
        .pc-cta {
          background: var(--bh-cta, var(--vibe-color-primary));
          color: var(--bh-cta-fg, #fff);
          border: 1.5px solid var(--bh-cta, var(--vibe-color-primary));
          border-radius: 9999px;
        }
        .pc-cta:hover {
          background: color-mix(in srgb, var(--bh-cta, var(--vibe-color-primary)) 88%, #000);
          border-color: color-mix(in srgb, var(--bh-cta, var(--vibe-color-primary)) 88%, #000);
          transform: translateY(-1px);
          box-shadow: 0 6px 18px color-mix(in srgb, var(--bh-cta, var(--vibe-color-primary)) 30%, transparent);
        }
      `}</style>
    </section>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'PinnedCallouts',
  displayName: 'Pinned Callouts',
  tokens: {
    'pinnedcallouts.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Find Your Island Escape</p>',
    },
    'pinnedcallouts.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultField: 'property_neighborhood_blurb',
      defaultValue:
        "<p>Perfectly positioned near the best of the neighborhood, our community puts dining, shopping, and entertainment just minutes from your front door. Whether you're commuting to work or exploring the city, everything you need is within easy reach.</p>",
    },
  },
  slotGroups: {
    'pinnedcallouts.cta': ctaDef,
  },
};

(PinnedCallouts as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PinnedCallouts;
